import { Link } from 'react-router-dom-v5-compat'

import { AccountType } from '@buffer-mono/app-shell/src/common/events/types'
import { getStartPageUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import {
  InstagramAuthMethod,
  Service,
} from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'
import {
  DropdownMenu,
  ExternalLinkIcon,
  FacebookIcon,
  LogOutIcon,
  PencilIcon,
  RefreshCwIcon,
  SettingsIcon,
  TrashIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { graphql } from '~publish/graphql'

import DisconnectChannelAlertDialog from './DisconnectChannelAlertDialog'
import { getChannelName } from '../../../helpers/channels'
import type { ChannelMask } from '../hooks/useChannelsForSettings'
import { useHasPrivilege } from '../hooks/useHasPrivilege'
import { Status } from '../util/getChannelStatus'
import { isStartPage } from '../util/isStartPage'
import { shouldShowRefreshButton } from '../util/shouldShowRefreshButton'

const RefreshConnectionMenuItem = ({
  channel,
  refreshChannelConnection,
}: {
  channel: ChannelMask
  refreshChannelConnection: (cta: string) => void
}): JSX.Element => {
  const canManageChannels = useHasPrivilege('canManageChannels')

  return (
    <DropdownMenu.Item
      onClick={(): void => {
        if (canManageChannels) {
          refreshChannelConnection(
            `publish-channels-menuItem-${channel.service.toLowerCase()}-1`,
          )
        }
      }}
      disabled={!canManageChannels}
    >
      <RefreshCwIcon />
      Refresh Connection
    </DropdownMenu.Item>
  )
}

export const RemoveChannelsMutation = graphql(/* GraphQL */ `
  mutation removeChannels($channelIds: [String!]) {
    channelsRemove(channelIds: $channelIds)
  }
`)

const StartPageMenuItems = ({
  channel,
  status,
}: {
  channel: ChannelMask
  status: Status
}): JSX.Element => {
  const canManageChannels = useHasPrivilege('canManageChannels')
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            window.location.assign(getStartPageUrl(`${channel.id}`))
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Start Page
      </DropdownMenu.Item>
      {status !== Status.Locked && (
        <DropdownMenu.Item
          onClick={(): void => {
            if (canManageChannels) {
              window.location.assign(getStartPageUrl(`${channel.id}/settings`))
            }
          }}
          disabled={!canManageChannels}
        >
          <LogOutIcon />
          Unpublish Channel
        </DropdownMenu.Item>
      )}
    </>
  )
}

const FacebookGroupMenuItems = ({
  channel,
}: {
  channel: ChannelMask
}): JSX.Element => {
  const canManageChannels = useHasPrivilege('canManageChannels')
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          const { MODALS, actions } = window?.appshell || {}
          if (canManageChannels) {
            actions.openModal(MODALS.facebookSelectAccountType, {
              accountType: AccountType.group,
              isRefreshingConnection: true,
              channelId: channel.id,
              channelName: channel.name,
              channelUrl:
                channel.metadata?.__typename === 'FacebookMetadata'
                  ? channel.metadata.locationData?.location ?? undefined
                  : undefined,
              channelAvatar: channel.avatar,
            })
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Group
      </DropdownMenu.Item>
    </>
  )
}

const InstagramPersonalProfileMenuItems = ({
  channel,
}: {
  channel: ChannelMask
}): JSX.Element => {
  const { MODALS, actions } = window?.appshell || {}
  const canManageChannels = useHasPrivilege('canManageChannels')
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            actions.openModal(MODALS.instagramSelectAccountType, {
              accountType: AccountType.personal,
              isRefreshingConnection: true,
              channelId: channel.id,
              channelName: channel.name,
              channelAvatar: channel.avatar,
            })
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Personal Account
      </DropdownMenu.Item>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            BufferTrackerReact.channelConversionStarted({
              channel: 'instagram',
              clientName: 'publishWeb',
              cta: 'publish-channels-menuItem-switchToAProfessionalAccount-1',
              organizationId: channel.organizationId,
            })

            actions.connectChannel({
              selectedService: Service.instagram,
              customChannelIdToConvert: channel.id,
              authMethod: InstagramAuthMethod.instagramLogin,
              cta: 'account-channels-menuItem-switchToAProfessionalAccount-1',
            })
          }
        }}
        disabled={!canManageChannels}
      >
        <RefreshCwIcon />
        Switch to Professional Account
      </DropdownMenu.Item>
    </>
  )
}

const InstagramLinkFacebookPageMenuItem = ({
  channel,
}: {
  channel: ChannelMask
}): JSX.Element => {
  const { MODALS, actions } = window?.appshell || {}
  const canManageChannels = useHasPrivilege('canManageChannels')

  return (
    <DropdownMenu.Item
      onClick={(): void => {
        if (canManageChannels) {
          actions.openModal(MODALS.instagramFacebookReAuth, {
            channelId: channel.id,
            channelType: channel.type,
          })
        }
      }}
      disabled={!canManageChannels}
    >
      <FacebookIcon />
      Link Account to Facebook Page
    </DropdownMenu.Item>
  )
}

interface ChannelDropdownItemProps {
  channel: ChannelMask
  status: Status
  refreshChannelConnection: (cta: string) => void
  showSettingsLink: boolean
}

const ChannelDropdownItems = ({
  channel,
  status,
  refreshChannelConnection,
  showSettingsLink,
}: ChannelDropdownItemProps): JSX.Element => {
  const canManageChannels = useHasPrivilege('canManageChannels')

  const isFacebookGroup =
    channel.service === 'facebook' && channel.type === 'group'
  const isInstagramPersonalProfile =
    channel.service === 'instagram' && channel.type === 'profile'
  const isChannelStartPage = isStartPage({ channel })
  const isInstagramLinkFacebookPageChannel =
    channel.service === 'instagram' && channel.apiVersion === 'instagramLogin'
  const shouldShowExternalLink = !!channel.externalLink

  const disconnectChannel = canManageChannels ? (
    <DisconnectChannelAlertDialog
      channel={channel}
      handleRefreshChannelConnection={refreshChannelConnection}
    >
      <DropdownMenu.Item
        variant="critical"
        onSelect={(e): void => e.preventDefault()}
      >
        <TrashIcon />
        {isChannelStartPage ? 'Delete Start Page' : 'Disconnect Channel'}
      </DropdownMenu.Item>
    </DisconnectChannelAlertDialog>
  ) : (
    <DropdownMenu.Item onSelect={(e): void => e.preventDefault()} disabled>
      <TrashIcon />
      {isChannelStartPage ? 'Delete Start Page' : 'Disconnect Channel'}
    </DropdownMenu.Item>
  )

  const externalLink = (
    <DropdownMenu.Item asChild>
      <a
        href={channel.externalLink ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExternalLinkIcon />
        {isChannelStartPage
          ? 'View Start Page'
          : `View on ${getChannelName(channel.service)}`}
      </a>
    </DropdownMenu.Item>
  )

  const shouldOnlyShowDisconnect =
    status === Status.Locked &&
    !isChannelStartPage &&
    !isFacebookGroup &&
    !isInstagramPersonalProfile

  // Special cases for locked channels with only a disconnect option
  if (shouldOnlyShowDisconnect) {
    if (shouldShowExternalLink) {
      return (
        <>
          {externalLink}
          <DropdownMenu.Separator />
          {disconnectChannel}
        </>
      )
    }
    return disconnectChannel
  }

  return (
    <>
      {shouldShowExternalLink && externalLink}
      {showSettingsLink && (
        <DropdownMenu.Item asChild>
          <Link to={`/channels/${channel.id}/settings`}>
            <SettingsIcon />
            Channel Settings
          </Link>
        </DropdownMenu.Item>
      )}
      {isChannelStartPage && (
        <StartPageMenuItems channel={channel} status={status} />
      )}
      {isFacebookGroup && <FacebookGroupMenuItems channel={channel} />}
      {isInstagramPersonalProfile && (
        <InstagramPersonalProfileMenuItems channel={channel} />
      )}
      {isInstagramLinkFacebookPageChannel && (
        <InstagramLinkFacebookPageMenuItem channel={channel} />
      )}
      {shouldShowRefreshButton({
        channel: {
          id: channel.id,
          service: channel.service,
          type: channel.type,
        },
        status,
      }) && (
        <RefreshConnectionMenuItem
          channel={channel}
          refreshChannelConnection={refreshChannelConnection}
        />
      )}
      <DropdownMenu.Separator />
      {disconnectChannel}
    </>
  )
}

export default ChannelDropdownItems
